import { Link } from 'react-router-dom';
import { Button } from 'antd';
import { EyeOutlined } from '@ant-design/icons';

import { highlightSearchText } from '../../../utils/searchHighlight';
import {
  SEARCH_TYPE_META,
  getSearchResultDetailPath,
  getSearchResultSubtitle,
  getSearchResultTags,
  getSearchResultTitle,
} from './searchResultMeta';

const TAG_COLOR_CLASSES = {
  default: 'border-slate-200 bg-slate-50 text-slate-600 dark:border-white/10 dark:bg-white/[0.06] dark:text-slate-300',
  blue: 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300',
  green: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300',
  orange: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300',
  cyan: 'border-cyan-200 bg-cyan-50 text-cyan-700 dark:border-cyan-500/20 dark:bg-cyan-500/10 dark:text-cyan-300',
  purple: 'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-500/20 dark:bg-violet-500/10 dark:text-violet-300',
  red: 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300',
  gold: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300',
};

function tagClassName(color) {
  return TAG_COLOR_CLASSES[color] || TAG_COLOR_CLASSES.default;
}

const SearchResultCard = ({ type, item, compact = false, onSelect }) => {
  const meta = SEARCH_TYPE_META[type] || SEARCH_TYPE_META.meetings;
  const Icon = meta.icon;
  const detailPath = getSearchResultDetailPath(type, item);
  const title = getSearchResultTitle(type, item);
  const subtitle = getSearchResultSubtitle(type, item);
  const tags = getSearchResultTags(type, item);

  if (compact) {
    const body = (
      <div className="flex min-w-0 items-start gap-2.5 px-3 py-2 text-right">
        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm ${meta.iconClass}`}>
          <Icon />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">{title}</div>
          <div className="mt-0.5 truncate text-[11px] text-slate-500 dark:text-slate-400">
            {meta.label}
            {item.timecode ? ` · ${item.timecode}` : ''}
            {item.snippet ? ` · ${item.snippet}` : subtitle ? ` · ${subtitle}` : ''}
          </div>
        </div>
      </div>
    );

    if (detailPath) {
      return (
        <Link
          to={detailPath}
          className="block no-underline transition-colors hover:bg-slate-50 dark:hover:bg-white/[0.05]"
          onClick={onSelect}
          onMouseDown={(e) => e.preventDefault()}
        >
          {body}
        </Link>
      );
    }

    return (
      <div className="block" onMouseDown={(e) => e.preventDefault()}>
        {body}
      </div>
    );
  }

  return (
    <article className="block w-full rounded-xl border border-slate-100 bg-white/75 p-3.5 text-right transition-colors hover:border-slate-300 hover:shadow-sm dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-white/20">
      <div className="flex items-start gap-3">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-base ${meta.iconClass}`}>
          <Icon />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="m-0 text-sm font-semibold leading-6 text-slate-800 dark:text-slate-100">{title}</h4>
          {type === 'transcripts' && item.snippet ? (
            <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">
              {highlightSearchText(item.snippet, item.query)}
            </p>
          ) : subtitle ? (
            <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">{subtitle}</p>
          ) : null}
          {tags.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {tags.map((tag) => (
                <span
                  key={tag.key}
                  className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[11px] font-medium ${tagClassName(tag.color)}`}
                >
                  {tag.label}
                </span>
              ))}
            </div>
          ) : null}
        </div>
        {detailPath ? (
          <Link to={detailPath} className="shrink-0 no-underline">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              className="!h-8 !rounded-lg !text-xs !text-emerald-600 hover:!bg-emerald-50 dark:!text-emerald-400 dark:hover:!bg-emerald-500/10"
            >
              جزئیات
            </Button>
          </Link>
        ) : null}
      </div>
    </article>
  );
};

export default SearchResultCard;
